import React, { useState, useEffect, useContext } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';

import { UserContext } from './hooks/UserContext';
import { fetchIngestionHealth, fetchTuning, inspectUser } from './api/insights';
import { fetchCategories } from './api/courses';
import Header from './components/Header'
import Loading from './components/Loading'

// Personalization insights (comfort-zone plan, S3). Read-only view over
// the signal logs and the live scoring: ingestion health, ranked-slot
// performance, and a per-user inspector whose numbers come from the same
// scoring path as the real feed. Tuning weights stays a config change.

// Windows offered by the range control. The server clamps to 1-365 and
// caches per window, so these stay a small fixed set.
const DAY_OPTIONS = [
    { label: 'Last 7 days', value: 7 },
    { label: 'Last 30 days', value: 30 },
    { label: 'Last 90 days', value: 90 },
];

const tableStyle = { borderCollapse: 'collapse', marginBottom: '1.5rem' };
const cellStyle = {
    border: '1px solid #ddd', padding: '4px 10px', textAlign: 'right',
};
const headStyle = { ...cellStyle, background: '#f4f4f4', textAlign: 'left' };

// Accepts epoch ms (events) or an ISO string (last_received_at).
const fmtTs = (at) => (at ? new Date(at).toLocaleString() : '—');
const fmtRate = (r) => (r === null || r === undefined ? '—' : `${(r * 100).toFixed(1)}%`);

// Friendly names for the feed experiment groups; the API keys stay
// treatment/control (the vocabulary of the events).
const ARM_LABELS = {
    treatment: 'Personalized feed',
    control: 'Old feed (comparison)',
};

// One widget's loading / error / loaded states. Keeping this per widget
// is what lets ingestion health paint while the tuning aggregates are
// still running, and stops one failed call blanking the page.
const Widget = ({ state, children }) => {
    if (state.loading) return <Loading />;
    if (state.error) return <p style={{ color: 'crimson' }}>{state.error}</p>;
    if (!state.data) return null;
    return children(state.data);
};

export const PersonalizationInsights = () => {
    const [days, setDays] = useState(7);

    // One slot per widget: the page paints each as it lands instead of
    // blocking on the slowest call, and a failure is scoped to the
    // widget that failed rather than blanking the dashboard.
    const [health, setHealth] = useState({ data: null, loading: true, error: '' });
    const [tuning, setTuning] = useState({ data: null, loading: true, error: '' });
    const [categories, setCategories] = useState({}); // id -> name

    const [userId, setUserId] = useState('');
    const [inspecting, setInspecting] = useState(false);
    const [inspection, setInspection] = useState(null);
    const [message, setMessage] = useState('');

    const { user } = useContext(UserContext);
    const token = user.token;

    useEffect(() => {
        let live = true;
        const load = (fetcher, set) => {
            set({ data: null, loading: true, error: '' });
            fetcher(token, days).then(
                (data) => live && set({ data, loading: false, error: '' }),
                (error) => {
                    console.error(error);
                    if (live) set({ data: null, loading: false, error: error.message });
                },
            );
        };
        load(fetchIngestionHealth, setHealth);
        load(fetchTuning, setTuning);
        return () => { live = false; };
    }, [token, days]);

    // Category names are window-independent, so they load once.
    useEffect(() => {
        let live = true;
        fetchCategories(token).then(
            (items) => {
                if (!live) return;
                const map = {};
                for (const c of items) map[c.id] = c.name;
                setCategories(map);
            },
            (error) => console.error(error),
        );
        return () => { live = false; };
    }, [token]);

    const inspect = async () => {
        if (!userId.trim()) return;
        setInspecting(true);
        setInspection(null);
        setMessage('');
        try {
            setInspection(await inspectUser(token, userId.trim()));
        } catch (error) {
            console.error(error);
            setMessage('Failed to inspect user.');
        } finally {
            setInspecting(false);
        }
    };

    const catName = (id) => categories[id] || `#${id}`;

    return (
        <div>
            <Header />
            <div style={{ padding: '1rem 2rem' }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    flexWrap: 'wrap',
                }}>
                    <h2 style={{ margin: 0 }}>Personalization insights</h2>
                    <Dropdown
                        value={days}
                        options={DAY_OPTIONS}
                        onChange={(e) => setDays(e.value)}
                        aria-label="Time window"
                    />
                </div>
                {message && <p style={{ color: 'crimson' }}>{message}</p>}

                <h3>Ingestion health (last {days} days)</h3>
                <Widget state={health}>
                    {(data) => (
                        <>
                            <p>
                                {data.window_events} events in the window
                                {' '}· last received:{' '}
                                {data.last_received_at
                                    ? fmtTs(data.last_received_at) : 'never'}
                            </p>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={headStyle}>Day</th>
                                        <th style={headStyle}>Events</th>
                                        <th style={headStyle}>Users</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.events_per_day.map(row => (
                                        <tr key={row.day}>
                                            <td style={cellStyle}>{row.day}</td>
                                            <td style={cellStyle}>{row.events}</td>
                                            <td style={cellStyle}>{row.active_users}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </Widget>

                <h3>Ranked-slot performance (last {days} days)</h3>
                <Widget state={tuning}>
                    {(data) => (
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={headStyle}>Slot</th>
                                    <th style={headStyle}>Served</th>
                                    <th style={headStyle}>Opened</th>
                                    <th style={headStyle}>Open rate</th>
                                    <th style={headStyle}>Sub-topic finished</th>
                                    <th style={headStyle}>Finish rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(data.slot_performance).map(
                                    ([reason, s]) => (
                                        <tr key={reason}>
                                            <td style={{ ...cellStyle, textAlign: 'left' }}>{reason}</td>
                                            <td style={cellStyle}>{s.served}</td>
                                            <td style={cellStyle}>{s.opened}</td>
                                            <td style={cellStyle}>{fmtRate(s.open_rate)}</td>
                                            <td style={cellStyle}>{s.topic_completed}</td>
                                            <td style={cellStyle}>{fmtRate(s.complete_rate)}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    )}
                </Widget>

                <h3>Engagement by category (last {days} days)</h3>
                <Widget state={tuning}>
                    {(data) => (
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={headStyle}>Category</th>
                                    <th style={headStyle}>Topic opens</th>
                                    <th style={headStyle}>Sub-topics finished</th>
                                    <th style={headStyle}>Topics finished</th>
                                    <th style={headStyle}>Bookmarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(data.category_engagement).map(
                                    ([cat, counts]) => (
                                        <tr key={cat}>
                                            <td style={{ ...cellStyle, textAlign: 'left' }}>{catName(cat)}</td>
                                            <td style={cellStyle}>{counts.open || 0}</td>
                                            <td style={cellStyle}>{counts.topic_completed || 0}</td>
                                            <td style={cellStyle}>{counts.course_completed || 0}</td>
                                            <td style={cellStyle}>{counts.bookmark || 0}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    )}
                </Widget>

                <h3>User inspector</h3>
                <p style={{ maxWidth: '48rem' }}>
                    The profile exactly as the feed endpoint computes it, plus a
                    feed preview (composed without logging servings — inspecting
                    never distorts the user's exposure signal).
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    <InputText
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        placeholder="user_id (UUID)"
                        style={{ width: '24rem' }}
                        onKeyDown={(e) => e.key === 'Enter' && inspect()}
                    />
                    <Button label="Inspect" onClick={inspect} loading={inspecting} />
                </div>

                {inspection && !inspection.has_profile && (
                    <p>
                        No signals for this user yet.
                        {' '}Feed group: <b>{ARM_LABELS[inspection.arm] || inspection.arm}</b>
                    </p>
                )}

                {inspection && inspection.has_profile && (
                    <div>
                        <p>
                            Feed group: <b>{ARM_LABELS[inspection.arm] || inspection.arm}</b>
                            {' '}· comfortable length: <b>{inspection.length_bucket || 'unknown'}</b>
                            {' '}· resting from row 1: <b>
                                {inspection.rested_top_courses.length
                                    ? inspection.rested_top_courses.join(', ')
                                    : 'none'}
                            </b>
                        </p>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={headStyle}>Category</th>
                                    <th style={headStyle}>Score (likes)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(inspection.category_scores)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([cat, score]) => (
                                        <tr key={cat}>
                                            <td style={{ ...cellStyle, textAlign: 'left' }}>{catName(cat)}</td>
                                            <td style={cellStyle}>{score}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>

                        <h4>Feed preview</h4>
                        {inspection.feed_preview.map(section => (
                            <p key={section.key} style={{ margin: '0.25rem 0' }}>
                                <b>{section.key}</b>
                                {section.count ? ` (show ${section.count})` : ''}:{' '}
                                {section.items.map(i =>
                                    `${i.course_id}${i.reason ? ` (${i.reason})` : ''}`
                                ).join(', ') || '—'}
                            </p>
                        ))}

                        <h4>Recent events</h4>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={headStyle}>When</th>
                                    <th style={headStyle}>Type</th>
                                    <th style={headStyle}>Course</th>
                                    <th style={headStyle}>Slot</th>
                                    <th style={headStyle}>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inspection.recent_events.map(e => (
                                    <tr key={e.id}>
                                        <td style={cellStyle}>{fmtTs(e.client_ts)}</td>
                                        <td style={{ ...cellStyle, textAlign: 'left' }}>{e.event_type}</td>
                                        <td style={cellStyle}>{e.course_id ?? e.category_id ?? ''}</td>
                                        <td style={{ ...cellStyle, textAlign: 'left' }}>{e.slot_source || ''}</td>
                                        <td style={cellStyle}>{e.value ?? ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <h4>Recent servings</h4>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={headStyle}>Day</th>
                                    <th style={headStyle}>Course</th>
                                    <th style={headStyle}>Slot</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inspection.recent_servings.map((s, i) => (
                                    <tr key={i}>
                                        <td style={cellStyle}>{s.day}</td>
                                        <td style={cellStyle}>{s.course_id}</td>
                                        <td style={{ ...cellStyle, textAlign: 'left' }}>{s.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PersonalizationInsights;
